import { GProviderDispatchStatus } from 'src/graphql/types/generated';
import { isCancelPending } from 'src/utils/burqRoute';
import { isCanceledOnProvider } from 'src/utils/providerDispatchStatus';
import { PolledDispatch, PolledRoute } from './useProviderDispatchPoll';

export type DialogView =
  | { kind: 'loading' }
  // `loadFailed`: the first poll failed and nothing is known; marking stays
  // safe because LDS refuses a route that is already marked.
  | { kind: 'form'; loadFailed: boolean }
  | { kind: 'pushFailed' }
  | { kind: 'waiting'; status: GProviderDispatchStatus }
  | { kind: 'quotes' }
  | { kind: 'quoteFailed' }
  | { kind: 'canceledOnProvider' }
  | { kind: 'dispatching' }
  // Running, and the provider would take a cancel now (cancelable).
  | { kind: 'running' }
  // A GM's cancel is out and the provider has not answered. `cancelable`: LDS
  // would take a new one, the old claim being past its stale bound.
  | { kind: 'canceling'; cancelable: boolean }
  | {
      kind: 'ended';
      reason: 'underway' | 'backWithOdeko' | 'superseded';
      status?: GProviderDispatchStatus;
    };

// A dispatched route. LDS keeps its status when the provider cancels it, and
// unmark is the way back while LDS says it would take one; otherwise a route
// LDS would cancel offers the cancel, and a pending one waits on its answer.
const liveDispatchView = (record: PolledDispatch): DialogView => {
  const keepAfterProviderCancel =
    isCanceledOnProvider(record.error) && record.unmarkable;
  if (record.status === GProviderDispatchStatus.Dispatched)
    return keepAfterProviderCancel
      ? { kind: 'canceledOnProvider' }
      : { kind: 'dispatching' };
  if (isCancelPending(record))
    return { kind: 'canceling', cancelable: record.cancelable };
  if (keepAfterProviderCancel) return { kind: 'canceledOnProvider' };
  if (record.cancelable) return { kind: 'running' };
  return { kind: 'ended', reason: 'underway', status: record.status };
};

// Before the poll has read the route. A failed first read still offers the
// form (marking is safe: LDS refuses a route already marked), except when the
// dialog was opened to cancel: that route is dispatched, never one to send,
// so it keeps waiting while the poll retries.
const beforeFirstRead = (
  pollFailed: boolean,
  openOn: 'cancel' | undefined,
): DialogView =>
  pollFailed && openOn !== 'cancel'
    ? { kind: 'form', loadFailed: true }
    : { kind: 'loading' };

// Which view the dialog shows for what the poll last read. `seenLive`: the
// dialog has seen this route in a live dispatch, so a canceled or completed
// row is news, not a leftover from before the dialog opened. `openOn`: the
// dialog was opened to cancel a dispatched route, so a canceled row is that
// route already back with Odeko, never a route to send again.
export const dialogViewFor = ({
  route,
  pollFailed,
  superseded,
  seenLive,
  openOn,
}: {
  route: PolledRoute | undefined;
  pollFailed: boolean;
  superseded: boolean;
  seenLive: boolean;
  openOn?: 'cancel';
}): DialogView => {
  if (superseded) return { kind: 'ended', reason: 'superseded' };
  if (!route) return beforeFirstRead(pollFailed, openOn);

  const record = route.providerDispatch;
  if (!record) return { kind: 'form', loadFailed: false };

  const canceledOnProvider = isCanceledOnProvider(record.error);
  switch (record.status) {
    case GProviderDispatchStatus.Canceled:
      return seenLive || openOn === 'cancel'
        ? { kind: 'ended', reason: 'backWithOdeko', status: record.status }
        : { kind: 'form', loadFailed: false };
    case GProviderDispatchStatus.Completed:
      return seenLive
        ? { kind: 'ended', reason: 'underway', status: record.status }
        : { kind: 'form', loadFailed: false };
    case GProviderDispatchStatus.InProgress:
    case GProviderDispatchStatus.Dispatched:
      return liveDispatchView(record);
    case GProviderDispatchStatus.PushFailed:
      return { kind: 'pushFailed' };
    case GProviderDispatchStatus.Pushing:
      return { kind: 'waiting', status: record.status };
    case GProviderDispatchStatus.Quoting:
      return canceledOnProvider
        ? { kind: 'canceledOnProvider' }
        : { kind: 'waiting', status: record.status };
    case GProviderDispatchStatus.QuoteFailed:
      return canceledOnProvider
        ? { kind: 'canceledOnProvider' }
        : { kind: 'quoteFailed' };
    case GProviderDispatchStatus.Quoted:
      return canceledOnProvider
        ? { kind: 'canceledOnProvider' }
        : { kind: 'quotes' };
    // A status this build does not know: show it and stop polling.
    default:
      return { kind: 'ended', reason: 'underway', status: record.status };
  }
};

// Statuses that make a later canceled or completed row news.
export const isLiveStatus = (status: GProviderDispatchStatus): boolean =>
  status !== GProviderDispatchStatus.Canceled &&
  status !== GProviderDispatchStatus.Completed;

// The views a cancel confirm can stand over: a route LDS would cancel now.
export const isCancelConfirmable = (view: DialogView): boolean =>
  view.kind === 'running' || (view.kind === 'canceling' && view.cancelable);
